import SuperRecordStore from "./SuperRecordStore";
import BaseServices from "lib/BaseServices";
import PropTypes from "prop-types";
import Q from "lib/Q";
import LoadObject from "data/LoadObject";
import TableStore from "data/TableStore";
import { recordIdType } from "data/util/typedefs";
import typedAction from "data/util/typedAction";
import { OPT_USER_CONFIRMED } from "data/RecordActions";
import EntityName from "data/util/EntityName";
import ClientId from "data/ClientId";
import { superRecordPacketType } from "data/selectors/superRecords";

const singleRecordRequestType = {
    key: PropTypes.string.isRequired, // stereotype key
    id: recordIdType.isRequired,
    initiatingContext: PropTypes.string,
};
const singleRecordResponseType = {
    ...singleRecordRequestType,
    data: superRecordPacketType.isRequired,
};
const singleRecordErrorType = {
    ...singleRecordRequestType,
    error: PropTypes.object.isRequired,
};

const SuperRecordActions = {
    /*
     * async triples
     */
    START_LOAD: "super-record/start-load",
    LOADED: typedAction("super-record/loaded", singleRecordResponseType),
    LOAD_ERROR: "super-record/load-error",

    START_CREATE: typedAction("super-record/start-create", singleRecordRequestType),
    CREATED: typedAction("super-record/created", singleRecordResponseType),
    CREATE_ERROR: typedAction("super-record/create-error", singleRecordErrorType),

    START_UPDATE: typedAction("super-record/start-update", singleRecordRequestType),
    UPDATED: typedAction("super-record/updated", singleRecordResponseType),
    UPDATE_ERROR: typedAction("super-record/update-error", singleRecordErrorType),

    // this might be for an unsaved record
    DELETE_RECORD: typedAction("super-record/delete-record", {
        ...singleRecordRequestType,
        [OPT_USER_CONFIRMED]: PropTypes.bool,
    }),
    DELETED: "super-record/deleted",
    DELETE_ERROR: typedAction("super-record/delete-error", singleRecordErrorType),

    /*
     * user actions
     */
    NEW: typedAction("super-record/new", {
        stereoKey: PropTypes.string.isRequired,
        recordKey: EntityName.propType.isRequired,
        newId: ClientId.propType.isRequired,
        navigate: PropTypes.bool,
    }),
    NEW_MAGIC_CHILD: typedAction("super-record/new-magic-child", {
        recordKey: PropTypes.string.isRequired,
        recordId: recordIdType.isRequired,
        entityKey: EntityName.propType.isRequired,
        newId: ClientId.propType.isRequired,
        navigate: PropTypes.bool,
    }),
    OPEN: typedAction("super-record/open", {
        key: PropTypes.string.isRequired,
        id: recordIdType.isRequired,
        entityKey: PropTypes.any,
        recordId: PropTypes.any,//PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        fieldKey: PropTypes.any
    }),
    SAVE: "super-record/save",
    CANCEL: "super-record/cancel",
    CANCEL_FOR_OPTLOCK: "super-record/cancel-for-optlock",
};

/**
 * I return a LoadObject containing a client-filtered list as an array of
 * EntityProxy. DO NOT TRUST THE PROMISE INSIDE.
 */
export const clientFilteredList = (
    SuperRecordListStore,
    TableStoreOrAccess,
    entityKey,
    accept,
) => {
    const idsLO = SuperRecordListStore.getListLO(entityKey);
    if (! idsLO.hasValue()) return idsLO;
    const recordsLO = recordsFromServer(
        SuperRecordStore,
        TableStoreOrAccess,
        idsLO.getValueEnforcing(),
        entityKey,
    );
    return accept == null
        ? recordsLO
        : recordsLO.map(rs => rs.filter(accept));
};

/**
 * I return a LoadObject containing a client-filtered search as an array of
 * EntityProxy. DO NOT TRUST THE PROMISE INSIDE.
 */
export const clientFilteredSearch = (
    SearchResultStore,
    entityKey,
    query,
    order,
    limit,
    accept = r => true,
    showLoadingModal = true
) => {
    const results = [];
    let page = 0;
    let isFinalPage = false;
    const isQ = query instanceof Q;
    const queryAS = isQ ? query.toAS() : query;
    const nextPage = () => {
        page += 1;
        if (page === 10) {
            console.warn("We're to page 10; is something wrong?");
        } else if (page === 20) {
            console.error("We're to page 20; something is wrong.");
        }
        const searchLO = SearchResultStore.getSearchLO(entityKey, queryAS, order, limit, page, showLoadingModal);
        if (searchLO.hasValue()) {
            const data = searchLO.getRequiredValue();
            if (data.results.length < data.pageSize) {
                isFinalPage = true;
            }
            const recordsLO = recordsFromServer(
                SuperRecordStore,
                TableStore,
                data.results.map(r => r.id),
                entityKey,
            );
            if (recordsLO.hasValue()) {
                let rs = recordsLO.getRequiredValue();
                if (isQ && BaseServices.__mocked) {
                    rs = rs.filter(query.test);
                }
                for (const r of rs) {
                    if (accept == null || accept(r)) {
                        results.push(r);
                        if (results.length >= limit) {
                            break;
                        }
                    }
                }
                if (results.length < limit && !isFinalPage) {
                    return nextPage();
                } else {
                    return LoadObject.withValue(results);
                }
            } else if (recordsLO.isPending()) {
                return recordsLO;
            } else if (recordsLO.hasError()) {
                return recordsLO;
            }
        } else if (searchLO.isPending()) {
            return searchLO;
        } else if (searchLO.hasError()) {
            return searchLO;
        }
    };
    return nextPage();
};

export const serverFilteredSearch = (
    SearchResultStore,
    entityKey,
    query,
    order,
    limit,
    page = 1,
    showLoadingModal = true,
    onlyEnabledRecords = false,
) => {
    const results = [];
    const isQ = query instanceof Q;
    const queryAS = isQ ? query.toAS() : query;
    const searchLO = SearchResultStore.getSearchLO(entityKey, queryAS, order, limit, page, showLoadingModal, onlyEnabledRecords);
    if (searchLO.hasValue()) {
        const data = searchLO.getRequiredValue();
        const recordsLO = recordsFromServer(
            SuperRecordStore,
            TableStore,
            data.results.map(r => r.id),
            entityKey,
        );
        if (recordsLO.hasValue()) {
            let rs = recordsLO.getRequiredValue();
            if (isQ && BaseServices.__mocked) {
                rs = rs.filter(query.test);
            }
            for (const r of rs) {
                results.push(r);
                if (results.length >= limit) {
                    break;
                }
            }
            return LoadObject.withValue(results);
        } else if (recordsLO.isPending()) {
            return recordsLO;
        } else if (recordsLO.hasError()) {
            return recordsLO;
        }
    } else if (searchLO.isPending()) {
        return searchLO;
    } else if (searchLO.hasError()) {
        return searchLO;
    }
};

const MAX_CONCURRENT = 4;

/**
 * I return a LoadObject containing an EntityProxy or an array of them. DO NOT
 * TRUST THE PROMISE INSIDE.
 */
const recordsFromServer = (
    SuperRecordStore,
    tableStoreOrAccess,
    idOrIds,
    entityKey
) => {
    if (idOrIds == null || idOrIds.length === 0) {
        return LoadObject.withValue([]);
    }
    const multiValued = Array.isArray(idOrIds);
    const ids = multiValued ? idOrIds : [idOrIds];
    if(ids.length > 11) { // only do this work when we need to.
        // only message once when actually loading records from the server.
        // There is probably a more elegant way of doing this.
        const uniqueMessageId = entityKey + ids.join(",");
        if(!messagingCache[uniqueMessageId]) {
            // how many records are loaded vs need to load.
            const loadCount = ids.length - ids.filter(id =>
                tableStoreOrAccess.hasEntityRecord(entityKey, id)).length;
            if (loadCount > 21) {
                console.warn("Pulling", ids.length, entityKey, "records. That's a LOT.");
            } else if (loadCount > 11) {
                console.log("Pulling", ids.length, entityKey, "records. That's quite a few.");
            }
        }
        messagingCache[uniqueMessageId] = true;
    }
    const eps = [];
    let inFlight = 0;
    for (const id of ids) {
        if (tableStoreOrAccess.hasEntityRecord(entityKey, id)) {
            eps.push(tableStoreOrAccess.getEntityProxy(entityKey, id));
        } else {
            const lo = SuperRecordStore.getRecordLO(entityKey, id);
            if (lo.hasValue()) {
                // must be inside a store listening for the API response, but
                // TableStore hasn't processed it yet...
                if (process.env.NODE_ENV !== "production") {
                    const d = SuperRecordStore.getDispatcher();
                    if (d.isDispatching()) {
                        const payload = d._pendingPayload; // tee-hee!
                        const type = payload == null ? "??UNKNOWN??" : payload.type;
                        console.warn("During dispatch of '" + type + "', TableStore was asked for the data it hasn't loaded yet (because it hasn't run yet).");
                    } else {
                        throw new Error("You've got some sort of need-a-record race.");
                    }
                }
                return LoadObject.empty()
                    .setError(ERR_NOT_YET_PROCESSED_BY_TABLESTORE)
                    .loading("garbage");
            } else if (lo.hasError()) {
                return lo;
            } else if (lo.isPending()) {
                inFlight += 1;
                if (inFlight >= MAX_CONCURRENT) {
                    break;
                }
            }
        }
    }
    if (inFlight > 0) {
        return LoadObject.loading("garbage");
    }
    return LoadObject.withValue(multiValued
        ? eps
        : eps.length === 0 ? null : eps[0]);
};

// hash table to manage large recordsFromServer requests messaging.
const messagingCache = {};

export const ERR_NOT_YET_PROCESSED_BY_TABLESTORE = "Client-filtered list requested during dispatch loading a record in the list. Try again.";

export default SuperRecordActions;



// WEBPACK FOOTER //
// ./app/data/SuperRecordActions.js